import { type NexusSignalDefinition, defineEvent, defineStore, otto } from '@otto-ec/global-resources/nexus';
import type {
    ResolveInput,
    SelectionAssignment,
    SelectionChange,
    SelectionResult,
    SelectionState,
} from '@otto-ec/nexus-registry/selection';
import { log } from '@shared/log.browser.ts';
import type { EventsOutgoing } from '@tilelist/application/ports/EventsOutgoing.ts';
import type { SelectionUpdate } from '@tilelist/application/ports/SelectionEvents.ts';
import { createEventsOutgoing } from '@tilelist/services/events/EventsOutgoing.browser.ts';
import { LegacySelectionPublisher } from './LegacySelectionPublisher.browser.ts';
import { createProductionSelectionDependencies } from './SelectionDependencies.browser.ts';
import type {
    ResolvedSelectionResponse,
    SelectionDomAdapter,
    SelectionResolver,
    SelectionTrackingAdapter,
} from './SelectionDependencies.ts';
import {
    type ForwardSelectionSource,
    type SelectionNavigation,
    defaultSelectionNavigation,
} from './SelectionNavigation.browser.ts';
import { handleSelectionNavigation } from './SelectionNavigationHandler.browser.ts';
import { orderedPage, pageFromAttributes, selectionParams } from './SelectionState.ts';

export type SelectionDependencies = {
    resolver: SelectionResolver;
    dom: SelectionDomAdapter;
    tracking: SelectionTrackingAdapter;
    eventsOutgoing?: EventsOutgoing;
    keepHeurekaFragment?: () => boolean;
    trackUrl?: (url: string) => void;
    navigation?: SelectionNavigation;
};

const logger = log.scope('selection');

export class Selection {
    private latestOperation = 0;
    private current: NexusSignalDefinition<SelectionState> | undefined;
    private readonly changes = defineEvent<SelectionChange>();
    private readonly updateHandlers = new Set<(update: SelectionUpdate) => void | Promise<void>>();
    private readonly eventsOutgoing: EventsOutgoing;
    private readonly legacyPublisher: LegacySelectionPublisher;

    public constructor(private readonly dependencies: SelectionDependencies = createProductionSelectionDependencies()) {
        this.eventsOutgoing = dependencies.eventsOutgoing ?? createEventsOutgoing();
        this.legacyPublisher = new LegacySelectionPublisher(dependencies.dom, this.eventsOutgoing);
    }

    public register(): () => void {
        if (this.current) throw new Error('Selection is already registered');
        logger.debug('Registering selection');

        try {
            const navigation = this.dependencies.navigation ?? defaultSelectionNavigation;
            const state = navigation.initialSelection(this.dependencies.dom);
            this.current = defineStore<SelectionState>(state);
            const unregisterNamespace = otto.selection.assignNamespace({
                resolve: this.resolve,
                assign: this.assign,
                current: this.current,
                changes: this.changes,
            });
            const unsubscribeNavigation = navigation.subscribe(this.handleNavigation);
            void this.navigateCheckpoint(state, 'full', 'replace', true).catch(() => undefined);
            return () => {
                unsubscribeNavigation();
                unregisterNamespace();
            };
        } catch (error) {
            logger.error('Failed to register selection', error);
            this.current = undefined;
            throw error;
        }
    }

    public readonly resolve = async (input: ResolveInput): Promise<SelectionResult> => {
        logger.debug('Resolving selection', input);
        const result = await this.resolveAttributes(input.attributes);
        if (result.count !== 0) await this.navigateCheckpoint(result.selection, 'full');
        await publishLegacyResolvedBestEffort(this.legacyPublisher, result);
        return result;
    };

    public onUpdate(handler: (update: SelectionUpdate) => void | Promise<void>): () => void {
        this.updateHandlers.add(handler);
        return () => this.updateHandlers.delete(handler);
    }

    public currentSelection(): SelectionState {
        if (!this.current) throw new Error('Selection is not registered');
        return otto.selection.current.get();
    }

    public async navigate(selection: Omit<SelectionState, 'entryRule'>, source: ForwardSelectionSource): Promise<void> {
        const entryRule = this.currentSelection().entryRule;
        await this.navigateCheckpoint({ ...selection, entryRule }, source);
    }

    public readonly assign = async (input: SelectionAssignment): Promise<SelectionResult> => {
        /*              */
        return this.resolveAttributes({ rule: input.rule });
    };

    public async resolveLegacy(attributes: Record<string, string | string[]>): Promise<void> {
        logger.debug('Resolving legacy selection', attributes);
        const result = await this.resolveAttributes(attributes);
        if (result.count !== 0) await this.navigateCheckpoint(result.selection, 'full');
        await publishLegacyResolvedBestEffort(this.legacyPublisher, result);
    }

    private async resolveAttributes(attributes: Record<string, string | string[]>): Promise<SelectionResult> {
        const params = selectionParams(attributes);
        const page = pageFromAttributes(attributes);

        return this.perform(async (sequence) => {
            const response = await this.resolveSelection(params, sequence);
            const entryRule = otto.selection.current.get().entryRule;
            if (!entryRule) throw new Error('Failed to resolve rule: missing entry rule');

            return {
                selection: { rule: response.rule, page, url: response.url, entryRule },
                ...(response.count !== undefined ? { count: response.count } : {}),
                operationId: 'dummy',
            };
        });
    }

    private async perform(operation: (sequence: number) => Promise<SelectionResult>): Promise<SelectionResult> {
        const sequence = ++this.latestOperation;
        const result = await operation(sequence);
        this.ensureLatest(sequence);
        return result;
    }

    private readonly handleNavigation = (
        event: Parameters<Parameters<SelectionNavigation['subscribe']>[0]>[0],
        checkpoint: unknown,
    ): void => {
        handleSelectionNavigation(event, checkpoint, (update, destinationUrl) => {
            this.dependencies.trackUrl?.(destinationUrl);
            otto.selection.current.emit(update.selection, { retain: true });
            for (const handler of this.updateHandlers) void handler(update);

            const result: SelectionResult = {
                selection: {
                    rule: update.selection.rule,
                    page: orderedPage(update.selection.page),
                    url: update.selection.url,
                    entryRule: update.selection.entryRule,
                },
                operationId: 'dummy',
            };
            void publishLegacyResolvedBestEffort(this.legacyPublisher, result);
        });
    };

    private async navigateCheckpoint(
        selection: SelectionState,
        source: ForwardSelectionSource,
        history: 'push' | 'replace' = 'push',
        initialize = false,
    ): Promise<void> {
        try {
            await (this.dependencies.navigation ?? defaultSelectionNavigation).navigate(
                selection,
                source,
                history,
                this.dependencies.keepHeurekaFragment?.() ?? false,
                initialize,
            );
        } catch (error) {
            logger.error('Failed to navigate selection', error);
            throw error;
        }
    }

    private async resolveSelection(params: URLSearchParams, sequence: number): Promise<ResolvedSelectionResponse> {
        const response = await this.dependencies.resolver(params, sequence);
        this.ensureLatest(sequence);
        this.dependencies.dom.replaceHtml(response.html);
        try {
            await this.dependencies.tracking.replaceContext(response.url);
        } catch (error) {
            logger.error('Failed to replace selection tracking context', error);
        }
        /*                                                                                                 */
        this.ensureLatest(sequence);
        return response;
    }

    private ensureLatest(sequence: number): void {
        if (sequence !== this.latestOperation)
            throw new Error('Selection operation was superseded by a newer operation');
    }
}

async function publishLegacyResolvedBestEffort(
    publisher: LegacySelectionPublisher,
    result: SelectionResult,
): Promise<void> {
    try {
        await publisher.publish(result);
    } catch (error) {
        logger.error('Failed to publish legacy selection resolution', error);
    }
}
